import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmStatus } from '@app/interaction/confirm/confirm.component';
import { InteractionService } from '@app/interaction/interaction.service';
import { NotificationsService } from '@shared/notifications.service';
import { ChatParticipant, ResolutionCenterService } from '@app/resolution-center/resolution-center.service';

@Component({
    selector: 'app-manage-members-modal',
    templateUrl: './manage-members-modal.component.html',
    styleUrls: ['./manage-members-modal.component.scss'],
    standalone: false
})

export class ManageMembersModalComponent implements OnInit {
    @Input() chatRoomId: number = 0;
    @Input() memberCount: number = 0;
    @Output() close = new EventEmitter<void>();
    @Output() addNewMembers = new EventEmitter<void>();

    members: ChatParticipant[] = [];
    searchQuery: string = '';
    currentPage = 1;
    lastPage = 1;
    total = 0;
    isLoading = false;
    isClosing = false;
    private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private resolutionCenterService: ResolutionCenterService,
        private interaction: InteractionService,
        private notifications: NotificationsService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.loadMembers();
    }

    onBackdropClick(event: MouseEvent): void {
        if (event.target === event.currentTarget) {
            this.onClose();
        }
    }

    onClose(): void {
        this.isClosing = true;
        setTimeout(() => {
            this.close.emit();
            this.isClosing = false;
        }, 180);
    }

    async loadMembers(): Promise<void> {
        if (!this.chatRoomId) return;

        this.isLoading = true;
        const query = this.searchQuery.trim() || undefined;
        const { data, error } = await this.resolutionCenterService.getChatRoomMembers(
            this.chatRoomId,
            query,
            this.currentPage
        );

        this.isLoading = false;
        if (error || !data) {
            this.members = [];
            return;
        }

        this.members = data.data || [];
        this.currentPage = data.current_page;
        this.lastPage = data.last_page;
        this.total = data.total;
    }

    onSearchInput(): void {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }
        this.searchDebounceTimer = setTimeout(() => {
            this.currentPage = 1;
            this.loadMembers();
            this.searchDebounceTimer = null;
        }, 300);
    }

    onPageChange(page: number): void {
        if (page < 1 || page > this.lastPage) return;
        this.currentPage = page;
        this.loadMembers();
    }

    getPaginationPages(): (number | string)[] {
        const pages: (number | string)[] = [];
        const totalPages = this.lastPage;
        const currentPage = this.currentPage;

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            if (currentPage > 3) {
                pages.push('...');
            }

            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            if (currentPage < totalPages - 2) {
                pages.push('...');
            }

            pages.push(totalPages);
        }

        return pages;
    }

    onPaginationPageClick(page: number | string): void {
        if (typeof page === 'number') {
            this.onPageChange(page);
        }
    }

    getMemberDisplayLine(member: ChatParticipant): string {
        const extended = member as ChatParticipant & { email?: string };
        return extended.email ?? member.role ?? '';
    }

    getInitials(name?: string): string {
        if (!name) return '?';
        const words = name.trim().split(/\s+/);
        if (words.length >= 2) {
            return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
        }
        return words[0].charAt(0).toUpperCase();
    }

    onOpenMemberProfile(member: ChatParticipant): void {
        if (!member?.id) return;
        const url = `/user/user/details-mantis/${member.id}`;
        window.open(url, '_blank');
    }

    async onRemoveMember(member: ChatParticipant): Promise<void> {
        if (!member?.id) return;

        const confirmed = await this.interaction.confirm({
            title: 'Remove member',
            message: 'Are you sure you want to remove this user from the group chat?<br><br>Once removed, they won\'t be able to access or view this group chat anymore.',
            renderAsHtml: true,
            buttons: [
                {
                    label: 'Cancel',
                    value: ConfirmStatus.CANCELED,
                    style: 'default'
                },
                {
                    label: 'Remove',
                    value: ConfirmStatus.CONFIRMED,
                    style: 'danger'
                }
            ]
        });

        if (!confirmed) return;

        const { error } = await this.resolutionCenterService.removeChatRoomMember(
            this.chatRoomId,
            member.id
        );

        if (error) {
            //error notification will be displayed anyway by the service
            return;
        }

        // Optimistically remove member locally without refetching from backend
        this.members = this.members.filter(m => m.id !== member.id);
        if (this.total > 0) {
            this.total = this.total - 1;
        }
        if (this.memberCount > 0) {
            this.memberCount = this.memberCount - 1;
        }

        this.notifications.success('Member removed', 'The user has been removed from the group chat.');
    }

    onOpenDirectChat(member: ChatParticipant): void {
        if (!member?.id) return;
        this.resolutionCenterService.openDirectChatRequest = { userId: member.id };
        this.router.navigate(['/client-messages']);
        this.onClose();
    }

    onAddNew(): void {
        this.addNewMembers.emit();
    }
}
